import React from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { DATE_FORMAT } from "utils/helper";
import { BULK_FIELDS } from "./helper";

const DictationSummaryToolbar = ({
  isDialog = false,
  selectedRowIds = [],
  bulkFields = [],
  bulkValue = "",
  selectedBulkType = "",
  bulkValueOptions = [],
  handleBulkFieldChange,
  setBulkValue,
  handleBulkUpdate,
  handleClearSelection,
  handleExport,
  loading = false,
  dos,
  setDos,
  updateSelectedDictations,
  handleIgnoreAll,
  handleUndoIgnore,
}) => {
  const goToPreviousDay = () => {
    setDos((prev) => dayjs(prev).subtract(1, "day"));
  };

  const goToNextDay = () => {
    setDos((prev) => {
      const nextDay = dayjs(prev).add(1, "day");
      return nextDay.isAfter(dayjs()) ? dayjs() : nextDay;
    });
  };

  return (
    <Box mb={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
      >
        {!isDialog && <Typography variant="h6">Dictation Summary</Typography>}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography
            variant="body2"
            sx={{
              minWidth: 90,
              fontWeight: 600,
              color: selectedRowIds.length ? "primary.main" : "text.secondary",
            }}
          >
            Selected: {selectedRowIds.length}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Bulk Update Fields</InputLabel>
            <Select
              multiple
              value={bulkFields}
              label="Bulk Update Fields"
              onChange={(e) => handleBulkFieldChange(e.target.value)}
              disabled={selectedRowIds.length === 0}
              renderValue={(selected) =>
                BULK_FIELDS.filter((field) => selected.includes(field.value))
                  .map((field) => field.label)
                  .join(", ")
              }
            >
              {BULK_FIELDS.map((field) => {
                const hasSelection = bulkFields.length > 0;
                const currentType = hasSelection
                  ? BULK_FIELDS.find((f) => f.value === bulkFields[0])?.type
                  : null;

                const disabled = hasSelection && field.type !== currentType;

                return (
                  <MenuItem
                    key={field.value}
                    value={field.value}
                    disabled={disabled}
                  >
                    {field.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>
              {selectedBulkType === "agent" ? "Select Agent" : "Select Value"}
            </InputLabel>
            <Select
              value={bulkValue}
              label={
                selectedBulkType === "agent" ? "Select Agent" : "Select Value"
              }
              onChange={(e) => setBulkValue(e.target.value)}
              disabled={selectedRowIds.length === 0 || !bulkFields.length}
            >
              {selectedBulkType === "agent" && (
                <MenuItem value="NA">
                  <em>N/A</em>
                </MenuItem>
              )}
              {bulkValueOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleBulkUpdate}
            size="small"
            disabled={
              selectedRowIds.length === 0 ||
              !bulkFields.length ||
              !bulkValue ||
              loading
            }
          >
            Apply Bulk Update
          </Button>

          <Button
            variant="outlined"
            onClick={handleClearSelection}
            size="small"
            disabled={
              selectedRowIds.length === 0 && !bulkFields.length && !bulkValue
            }
          >
            Clear
          </Button>
          <Button
            variant="outlined"
            disabled={selectedRowIds.length === 0}
            onClick={updateSelectedDictations}
            size="small"
          >
            Update Tracker
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleExport}
            disabled={loading}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={selectedRowIds.length === 0 || loading}
            onClick={handleIgnoreAll}
          >
            Ignore
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={selectedRowIds.length === 0 || loading}
            onClick={handleUndoIgnore}
          >
            Undo Ignore
          </Button>
        </Box>
      </Box>
      {!isDialog && (
        <>
          <IconButton
            aria-label="previous day"
            onClick={goToPreviousDay}
            size="small"
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>

          <DesktopDatePicker
            value={dayjs(dos)}
            onChange={(date) => setDos(date)}
            format={DATE_FORMAT}
            maxDate={dayjs()}
            slotProps={{
              textField: { variant: "standard", size: "small" },
            }}
          />

          <IconButton
            aria-label="next day"
            onClick={goToNextDay}
            size="small"
            disabled={dayjs(dos).isSame(dayjs(), "day")}
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default DictationSummaryToolbar;
